import { FC, useMemo, useState } from 'react';

import { PublicFeedEvent } from '@shared/types/event';
import dayjs from 'dayjs';
import { uniq, uniqBy } from 'lodash';

import {
  useFinalizeDynamicEventMutation,
  useRejectDynamicEventMutation,
} from '@admin/mutations/AdminDynamicEventsMutations';

import { getApiUrl } from '@api/API';

import { useUnauthorizedEventsQuery } from '@queries/EventQueries';
import { useSeries } from '@queries/TagQueries';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { MultipleChoiceList } from '@components/form/MultipleChoiceList';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';

// The rendered card image for a dynamic event, as opposed to a source event's thumbnail
const DYNAMIC_IMAGE_ID = 'dynamic';

// TODO: replace with a real mutation
const approveEventStub = async (payload: { eventId: string; sourceEventIds: string[] }) => {
  console.log('approveEventStub', payload);
};

const getEventThumbnailUrl = (eventId: string) => `${getApiUrl()}/v1/events/${eventId}/thumbnail`;

enum DynamicEventActionCardAction {
  Approve = 'approve',
  Reject = 'reject',
}

const ACTION_OPTIONS = [
  { value: DynamicEventActionCardAction.Approve, label: 'Approve this event' },
  { value: DynamicEventActionCardAction.Reject, label: 'Reject this event' },
];

type SeriesOption = { label: string; value: string };

interface ApproveEventFormProps {
  event: PublicFeedEvent;
  actionToken: string;
  isDynamic: boolean;
  sourceEventIds: string[];
  tagIds: string[];
  seriesOptions: SeriesOption[];
  onComplete: () => void;
}

const ApproveEventForm: FC<ApproveEventFormProps> = ({
  event,
  actionToken,
  isDynamic,
  sourceEventIds,
  tagIds,
  seriesOptions,
  onComplete,
}) => {
  const { isFinalizingDynamicEvent, finalizeDynamicEventAsync } = useFinalizeDynamicEventMutation();

  const imageOptions = useMemo(
    () => [
      ...(isDynamic && event.img ? [{ id: DYNAMIC_IMAGE_ID, src: event.img }] : []),
      ...sourceEventIds.map((id) => ({ id, src: getEventThumbnailUrl(id) })),
    ],
    [isDynamic, event.img, sourceEventIds],
  );
  const [imageEventId, setImageEventId] = useState<string>(imageOptions[0]?.id);

  const entertainerOptions = useMemo(
    () =>
      uniqBy(
        (event.entertainers ?? []).map((entertainer) => ({
          // Placeholders on dynamic events have no doc, so fall back to the title
          label: entertainer._id ? entertainer.title : `${entertainer.title} (No profile)`,
          value: entertainer._id || entertainer.title,
        })),
        'value',
      ),
    [event.entertainers],
  );
  const [claimedEntertainerIds, setClaimedEntertainerIds] = useState<string[]>(
    entertainerOptions.map(({ value }) => value),
  );

  // Only preselect series that are shown, so Finalize never submits a hidden selection
  const [seriesIds, setSeriesIds] = useState<string[]>(() => {
    const optionValues = new Set(seriesOptions.map(({ value }) => value));
    return uniq([...tagIds, ...(event.series ?? []).map(({ _id }) => _id)]).filter((id) =>
      optionValues.has(id),
    );
  });

  const handleFinalize = async () => {
    await finalizeDynamicEventAsync({
      actionToken,
      imageFromEventId: imageEventId,
      tagIds: seriesIds,
      // Placeholders are keyed by title and have no doc to attach
      entertainerIds: claimedEntertainerIds.filter((id) => event.entertainerIds.includes(id)),
    });
    onComplete();
  };

  return (
    <div className="mt-4">
      {imageOptions.length > 1 ? (
        <HorizontalScroll
          items={[
            <div className="d-flex gap-2">
              {imageOptions.map(({ id, src }) => (
                <div key={id} onClick={() => setImageEventId(id)}>
                  <img
                    src={src}
                    alt={event.title}
                    height={160}
                    style={{
                      borderRadius: 8,
                      border: imageEventId === id ? '4px solid var(--color-green)' : 'none',
                    }}
                  />
                </div>
              ))}
            </div>,
          ]}
        />
      ) : (
        imageOptions[0] && (
          <div>
            <img
              src={imageOptions[0].src}
              alt={event.title}
              height={160}
              style={{ borderRadius: 8 }}
            />
          </div>
        )
      )}

      {!!entertainerOptions.length && (
        <div className="mt-2">
          <div style={{ fontSize: 12, fontWeight: 500 }}>Entertainers</div>
          <MultipleChoiceList
            isMultiSelect
            options={entertainerOptions}
            defaultSelected={claimedEntertainerIds}
            onChange={setClaimedEntertainerIds}
          />
        </div>
      )}

      {!!seriesOptions.length && (
        <div className="mt-2">
          <div style={{ fontSize: 12, fontWeight: 500 }}>Series</div>
          <MultipleChoiceList
            isMultiSelect
            options={seriesOptions}
            defaultSelected={seriesIds}
            onChange={setSeriesIds}
          />
        </div>
      )}

      <div className="mt-4">
        <Button roundness="rounded" disabled={isFinalizingDynamicEvent} onClick={handleFinalize}>
          Finalize
        </Button>
      </div>
    </div>
  );
};

interface DynamicEventActionCardProps {
  event: PublicFeedEvent;
  tagIds: string[];
  seriesOptions: SeriesOption[];
}

const DynamicEventActionCard: FC<DynamicEventActionCardProps> = ({
  event,
  tagIds,
  seriesOptions,
}) => {
  const [didSubmit, setDidSubmit] = useState<boolean>(false);
  const { isRejectingDynamicEvent, rejectDynamicEventAsync } = useRejectDynamicEventMutation();
  const [selectedAction, setSelectedAction] = useState<DynamicEventActionCardAction>(
    DynamicEventActionCardAction.Approve,
  );

  const isDynamic = !!event.actionToken || !!event.sourceEventIds?.length;
  const sourceEventIds = useMemo(
    () => (event.sourceEventIds?.length ? event.sourceEventIds : [event._id]),
    [event.sourceEventIds, event._id],
  );

  const handleComplete = () => setDidSubmit(true);

  // Only dynamic events carry a token, so only they can be finalized or rejected here
  const { actionToken } = event;

  const handleReject = async () => {
    if (!actionToken) {
      return;
    }

    await rejectDynamicEventAsync({ actionToken, reason: '' });
    handleComplete();
  };

  const handleSelectAction = async (action: DynamicEventActionCardAction) => {
    setSelectedAction(action);
    if (action === DynamicEventActionCardAction.Approve) {
      await approveEventStub({ eventId: event._id, sourceEventIds });
    }
  };

  if (didSubmit) {
    return <></>;
  }

  return (
    <Card>
      <div>
        <Card.Header
          avatar={
            event.img ? (
              <img src={event.img} alt={event.title} height={60} style={{ borderRadius: 4 }} />
            ) : undefined
          }
          title={event.title}
          tagline={[
            isDynamic ? 'Dynamic' : event.isApproved ? 'Approved' : 'Pending',
            isDynamic && sourceEventIds.length > 1 ? `${sourceEventIds.length} sources` : '',
            event.confidenceLevel ?? '',
          ]
            .filter(Boolean)
            .join(' | ')}
          subtitle={event.timeDetails}
          action={
            actionToken ? (
              <IconButton
                color="danger"
                variant="smooth"
                disabled={isRejectingDynamicEvent}
                onClick={handleReject}
              >
                <FontAwesomeIcon name="times" />
              </IconButton>
            ) : undefined
          }
        />
        {actionToken && (
          <Card.Body>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500 }}>Action</div>
              <MultipleChoiceList
                options={ACTION_OPTIONS}
                selected={[selectedAction]}
                onChange={([updatedAction]) =>
                  handleSelectAction(updatedAction as DynamicEventActionCardAction)
                }
              />
            </div>

            {selectedAction === DynamicEventActionCardAction.Approve && (
              <ApproveEventForm
                event={event}
                actionToken={actionToken}
                isDynamic={isDynamic}
                sourceEventIds={sourceEventIds}
                tagIds={tagIds}
                seriesOptions={seriesOptions}
                onComplete={handleComplete}
              />
            )}

            {selectedAction === DynamicEventActionCardAction.Reject && (
              <div className="mt-4">
                <Button
                  roundness="rounded"
                  color="danger"
                  disabled={isRejectingDynamicEvent}
                  onClick={handleReject}
                >
                  Reject
                </Button>
              </div>
            )}
          </Card.Body>
        )}
      </div>
    </Card>
  );
};

export const SeriesManageEvents = ({ tagIds, cityId }: { tagIds: string[]; cityId: string }) => {
  const { eventsAreReady, events = [] } = useUnauthorizedEventsQuery(
    {
      tagIds,
      startDate: dayjs().format('YYYY-MM-DD'),
      forceSupply: 'true',
    },
    Boolean(tagIds?.length),
    {
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
    },
  );

  const { series = [], isFetched: isSeriesFetched } = useSeries();
  const seriesOptions = useMemo(
    () =>
      uniqBy(
        series
          .filter(({ slug }) =>
            ['denver-drag', 'denver-queer-calendar', 'queer-comedy-colorado'].includes(slug),
          )
          .map(({ _id, title }) => ({ label: title, value: _id })),
        'value',
      ),
    [series],
  );

  // Wait for series too, since each card preselects its series once on mount
  if (!eventsAreReady || !isSeriesFetched) {
    return <div>Loading...</div>;
  }

  if (!events.length) {
    return <EmptyState title="No events" content="No upcoming events for this series." />;
  }

  return (
    <div className="d-grid">
      {events.map((event) => (
        <DynamicEventActionCard
          key={event._id}
          event={event}
          tagIds={tagIds}
          seriesOptions={seriesOptions}
        />
      ))}
    </div>
  );
};
